import { businessSessionSchema, staffTodaySchema, type BusinessSession, type MyBusiness, type StaffToday } from "@teamna/shared";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";
import { useBusinesses } from "./use-member.ts";

/**
 * The staff side of Desk: the day of a person who runs sessions, and one session for the screen
 * that takes attendance. Ports of the mobile hooks in `apps/mobile/src/hooks/use-club.ts`.
 *
 * Marks go through `useMarkAttendance` in `use-groups.ts`, which refreshes `queryKeys.staff.all`
 * with the rest of the schedule.
 */

const base = (businessId: string) => `/business/businesses/${businessId}`;

/** GET /v1/business/businesses/:id/staff/today. The person's own sessions today, and the others. */
export function useStaffToday(businessId: string | null): UseQueryResult<StaffToday> {
  return useQuery({
    queryKey: queryKeys.staff.today(businessId ?? ""),
    queryFn: () => api.get(`${base(businessId!)}/staff/today`, staffTodaySchema),
    enabled: Boolean(businessId),
  });
}

/** GET /v1/business/businesses/:id/sessions/:sessionId. One session with its booked participants. */
export function useBusinessSession(businessId: string | null, sessionId: string): UseQueryResult<BusinessSession> {
  return useQuery({
    queryKey: queryKeys.staff.session(businessId ?? "", sessionId),
    queryFn: () => api.get(`${base(businessId!)}/sessions/${sessionId}`, businessSessionSchema),
    enabled: Boolean(businessId),
  });
}

const STAFF_KEY = "teamna-web-staff-business";

function readStaffStored(): string | null {
  try {
    return window.localStorage.getItem(STAFF_KEY);
  } catch {
    return null;
  }
}

let staffBusinessId: string | null = typeof window === "undefined" ? null : readStaffStored();
const staffListeners = new Set<() => void>();

/**
 * Which business the staff area shows, when the account is staff at two (picked on the "Where do
 * you want to go?" screen or in the account menu). Same shape as the owner and member stores.
 */
export function setStaffBusinessId(next: string | null): void {
  if (staffBusinessId === next) return;
  staffBusinessId = next;
  try {
    if (next === null) window.localStorage.removeItem(STAFF_KEY);
    else window.localStorage.setItem(STAFF_KEY, next);
  } catch {
    // Kept in memory only.
  }
  for (const listener of staffListeners) listener();
}

function subscribeStaff(listener: () => void): () => void {
  staffListeners.add(listener);
  return () => {
    staffListeners.delete(listener);
  };
}

const readStaff = () => staffBusinessId;

/**
 * The business a staff member runs sessions for, on the staff area of Desk: the one picked last,
 * or the first business where the account is staff.
 */
export function useStaffBusiness(): { business: MyBusiness | null; isLoading: boolean; error: unknown } {
  const businesses = useBusinesses();
  const stored = useSyncExternalStore(subscribeStaff, readStaff, readStaff);
  const business = useMemo(() => {
    const staffAt = (businesses.data ?? []).filter((row) => row.staffRole === "staff");
    return staffAt.find((row) => row.businessId === stored) ?? staffAt[0] ?? null;
  }, [businesses.data, stored]);
  return { business, isLoading: businesses.isLoading, error: businesses.error };
}
